import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Save, Folder, Calculator, Download, MessageCircle, Crown, User, LogOut } from 'lucide-react';
import './App.css';

interface ChartData {
  age: number;
  current: number;
  ideal: number;
}

interface SavedScenario {
  id: string;
  name: string;
  currentAge: number;
  targetAge: number;
  monthlyIncome: number;
  monthlyCost: number;
  currentSavings: number;
  createdAt: Date;
  wealthAtFreedom: number;
  monthsToFreedom: number;
  additionalNeeded: number;
  userId?: string;
}

interface User {
  id: string;
  anonymousName: string;
  avatar: string;
  createdAt: Date;
  lastActive: Date;
  isPremium: boolean;
  scenarios: SavedScenario[];
  totalCalculations?: number;
}

interface Message {
  id: string;
  userId: string;
  fromBanker: boolean;
  content: string;
  createdAt: Date;
  read: boolean;
}

interface CalculationLog {
  id: string;
  userId: string;
  userAnonymousName: string;
  timestamp: Date;
  sessionStartTime: Date;
  calculationNumber: number;
  inputAge: number;
  inputTargetAge: number;
  inputIncome: number;
  inputCost: number;
  inputSavings: number;
  resultCurrentWealth: number;
  resultIdealWealth: number;
  resultRequiredSavings: number;
  resultAdditionalNeeded: number;
  resultYearsToFreedom: number;
  timeSpentSeconds: number;
  wasSaved: boolean;
  isNewUser: boolean;
  userCreatedAt: Date;
  userTotalScenarios: number;
  isPremium: boolean;
}

const App: React.FC = () => {
  // User management
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userType, setUserType] = useState<'CLIENT' | 'BANKER'>('CLIENT');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  
  // Banker dashboard states
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedClient, setSelectedClient] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>('');
  const [showBankerDashboard, setShowBankerDashboard] = useState<boolean>(false);

  // Form states
  const [currentAge, setCurrentAge] = useState<string>('');
  const [targetFreedomAge, setTargetFreedomAge] = useState<string>('');
  const [monthlyIncome, setMonthlyIncome] = useState<number | null>(null);
  const [monthlyCost, setMonthlyCost] = useState<number | null>(null);
  const [currentSavings, setCurrentSavings] = useState<number | null>(null);

  // Results states
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [summary, setSummary] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [debugMonthlySurplus, setDebugMonthlySurplus] = useState<number>(0);
  const [requiredMonthlySavingsAmount, setRequiredMonthlySavingsAmount] = useState<number>(0);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);

  // Scenarios states
  const [savedScenarios, setSavedScenarios] = useState<SavedScenario[]>([]);
  const [showSavedScenarios, setShowSavedScenarios] = useState<boolean>(false);
  const [scenarioName, setScenarioName] = useState<string>('');
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);

  // Ranking states
  const [showRanking, setShowRanking] = useState<boolean>(false);

  // Analytics tracking
  const [sessionData, setSessionData] = useState<{
    startTime: Date;
    calculationsCount: number;
    timeSpent: number;
  }>({
    startTime: new Date(),
    calculationsCount: 0,
    timeSpent: 0
  });

  // Currency formatter
  const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2
  });

  const currencyFormatterNoDecimals = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0
  });

  useEffect(() => {
    initializeApp();
  }, []);

  const generateAnonymousUser = (): User => {
    const adjectives = ['Smart', 'Wise', 'Strategic', 'Ambitious', 'Focused', 'Determined', 'Visionary', 'Prudent'];
    const nouns = ['Investor', 'Saver', 'Planner', 'Builder', 'Achiever', 'Navigator', 'Strategist', 'Accumulator'];
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#F98A8A', '#87CEEB'];
    
    const randomId = Math.floor(Math.random() * 9999) + 1000;
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    
    return {
      id: `user_${Date.now()}_${randomId}`,
      anonymousName: `${randomAdjective} ${randomNoun} #${randomId}`,
      avatar: randomColor,
      createdAt: new Date(),
      lastActive: new Date(),
      isPremium: false,
      scenarios: [],
      totalCalculations: 0
    };
  };

  const initializeApp = () => {
    const savedUser = localStorage.getItem('legacyTrackUser');
    if (savedUser) {
      const user = JSON.parse(savedUser);
      setCurrentUser(user);
      setIsAuthenticated(true);
      loadUserScenarios(user.id);
    }
    
    const bankerCode = localStorage.getItem('bankerCode');
    if (bankerCode === 'LEGACY_BANKER_2025') {
      setUserType('BANKER');
      loadAllUsers();
    }
  };

  const createNewUser = () => {
    const newUser = generateAnonymousUser();
    setCurrentUser(newUser);
    setIsAuthenticated(true);
    localStorage.setItem('legacyTrackUser', JSON.stringify(newUser));
    saveUserToGlobal(newUser);
  };

  const saveUserToGlobal = (user: User) => {
    const allUsers = JSON.parse(localStorage.getItem('allLegacyUsers') || '[]');
    const existingIndex = allUsers.findIndex((u: User) => u.id === user.id);
    
    if (existingIndex >= 0) {
      allUsers[existingIndex] = { ...user, lastActive: new Date() };
    } else {
      allUsers.push(user);
    }
    
    localStorage.setItem('allLegacyUsers', JSON.stringify(allUsers));
  };

  const loadAllUsers = () => {
    const users = JSON.parse(localStorage.getItem('allLegacyUsers') || '[]');
    setAllUsers(users);
  };

  const loadUserScenarios = (userId: string) => {
    const scenarios = JSON.parse(localStorage.getItem(`scenarios_${userId}`) || '[]');
    setSavedScenarios(scenarios);
  };

  const enableBankerMode = () => {
    const code = prompt('Digite o código do banker:');
    if (code === 'LEGACY_BANKER_2025') {
      setUserType('BANKER');
      localStorage.setItem('bankerCode', code);
      loadAllUsers();
      setShowBankerDashboard(true);
    } else {
      alert('Código incorreto!');
    }
  };

  const logout = () => {
    setCurrentUser(null);
    setIsAuthenticated(false);
    setUserType('CLIENT');
    localStorage.removeItem('legacyTrackUser');
    localStorage.removeItem('bankerCode');
  };

  // Track every calculation (even without saving)
  const trackCalculation = (data: {
    currentAge: number;
    targetAge: number;
    monthlyIncome: number;
    monthlyCost: number;
    currentSavings: number;
    wealthAtFreedomCurrent: number;
    wealthAtFreedomIdeal: number;
    requiredMonthlySavings: number;
    additionalNeeded: number;
  }) => {
    if (!currentUser) return;

    const calculationLog: CalculationLog = {
      id: `calc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: currentUser.id,
      userAnonymousName: currentUser.anonymousName,
      timestamp: new Date(),
      sessionStartTime: sessionData.startTime,
      calculationNumber: sessionData.calculationsCount + 1,
      
      inputAge: data.currentAge,
      inputTargetAge: data.targetAge,
      inputIncome: data.monthlyIncome,
      inputCost: data.monthlyCost,
      inputSavings: data.currentSavings,
      
      resultCurrentWealth: data.wealthAtFreedomCurrent,
      resultIdealWealth: data.wealthAtFreedomIdeal,
      resultRequiredSavings: data.requiredMonthlySavings,
      resultAdditionalNeeded: data.additionalNeeded,
      resultYearsToFreedom: data.targetAge - data.currentAge,
      
      timeSpentSeconds: Math.floor((new Date().getTime() - sessionData.startTime.getTime()) / 1000),
      wasSaved: false,
      
      isNewUser: sessionData.calculationsCount === 0,
      userCreatedAt: currentUser.createdAt,
      userTotalScenarios: currentUser.scenarios.length,
      isPremium: currentUser.isPremium
    };

    const allCalculations = JSON.parse(localStorage.getItem('legacyTrackAnalytics') || '[]');
    allCalculations.push(calculationLog);
    localStorage.setItem('legacyTrackAnalytics', JSON.stringify(allCalculations));

    setSessionData(prev => ({
      ...prev,
      calculationsCount: prev.calculationsCount + 1,
      timeSpent: Math.floor((new Date().getTime() - prev.startTime.getTime()) / 1000)
    }));

    updateUserActivity();
  };

  const updateUserActivity = () => {
    if (!currentUser) return;
    
    const updatedUser = {
      ...currentUser,
      lastActive: new Date(),
      totalCalculations: (currentUser.totalCalculations || 0) + 1
    };
    
    setCurrentUser(updatedUser);
    saveUserToGlobal(updatedUser);
  };

  const markCalculationAsSaved = () => {
    const allCalculations = JSON.parse(localStorage.getItem('legacyTrackAnalytics') || '[]');
    
    if (allCalculations.length > 0) {
      const lastCalc = allCalculations[allCalculations.length - 1];
      if (lastCalc.userId === currentUser?.id) {
        lastCalc.wasSaved = true;
        localStorage.setItem('legacyTrackAnalytics', JSON.stringify(allCalculations));
      }
    }
  };

  const getAnalyticsData = () => {
    const allCalculations = JSON.parse(localStorage.getItem('legacyTrackAnalytics') || '[]');
    const today = new Date();
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const todayCalculations = allCalculations.filter((calc: CalculationLog) => 
      new Date(calc.timestamp).toDateString() === today.toDateString()
    );

    const weekCalculations = allCalculations.filter((calc: CalculationLog) => 
      new Date(calc.timestamp) >= lastWeek
    );

    const uniqueUsersToday = Array.from(new Set(todayCalculations.map((calc: CalculationLog) => calc.userId)));
    const uniqueUsersWeek = Array.from(new Set(weekCalculations.map((calc: CalculationLog) => calc.userId)));

    const conversionRate = allCalculations.length > 0 
      ? (allCalculations.filter((calc: CalculationLog) => calc.wasSaved).length / allCalculations.length * 100)
      : 0;

    const averageAge = allCalculations.length > 0
      ? allCalculations.reduce((sum: number, calc: CalculationLog) => sum + calc.inputAge, 0) / allCalculations.length
      : 0;

    const averageIncome = allCalculations.length > 0
      ? allCalculations.reduce((sum: number, calc: CalculationLog) => sum + calc.inputIncome, 0) / allCalculations.length
      : 0;

    const averageTargetAge = allCalculations.length > 0
      ? allCalculations.reduce((sum: number, calc: CalculationLog) => sum + calc.inputTargetAge, 0) / allCalculations.length
      : 0;

    return {
      totalCalculations: allCalculations.length,
      todayCalculations: todayCalculations.length,
      weekCalculations: weekCalculations.length,
      uniqueUsersToday: uniqueUsersToday.length,
      uniqueUsersWeek: uniqueUsersWeek.length,
      conversionRate: Math.round(conversionRate * 100) / 100,
      averageAge: Math.round(averageAge),
      averageIncome: Math.round(averageIncome),
      averageTargetAge: Math.round(averageTargetAge),
      recentCalculations: allCalculations
        .sort((a: CalculationLog, b: CalculationLog) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 20)
    };
  };

  const sendMessage = () => {
    if (!newMessage.trim() || !selectedClient) return;
    
    const message: Message = {
      id: Date.now().toString(),
      userId: selectedClient.id,
      fromBanker: userType === 'BANKER',
      content: newMessage,
      createdAt: new Date(),
      read: false
    };
    
    const existingMessages = JSON.parse(localStorage.getItem(`messages_${selectedClient.id}`) || '[]');
    existingMessages.push(message);
    localStorage.setItem(`messages_${selectedClient.id}`, JSON.stringify(existingMessages));
    
    setMessages(existingMessages);
    setNewMessage('');
  };

  const loadClientMessages = (userId: string) => {
    const clientMessages = JSON.parse(localStorage.getItem(`messages_${userId}`) || '[]');
    setMessages(clientMessages);
  };

  const inputsAreValid = (): boolean => {
    const age = parseInt(currentAge);
    const targetAge = parseInt(targetFreedomAge);
    
    return !!(age > 0 && 
              targetAge > age && 
              monthlyIncome && monthlyIncome > 0 && 
              monthlyCost !== null && monthlyCost >= 0 && 
              currentSavings !== null && currentSavings >= 0);
  };

  const requiredMonthlySavings = (
    currentAge: number,
    targetFreedomAge: number,
    currentSavings: number,
    monthlyCost: number,
    finalAge: number,
    monthlyReturn: number
  ): number => {
    const monthsToFreedom = (targetFreedomAge - currentAge) * 12;
    const monthsInRetirement = (finalAge - targetFreedomAge) * 12;
    
    const precision = 10.0;
    let low = 0.0;
    let high = 100000.0;
    
    while (high - low > precision) {
      const totalMonthlySavings = (low + high) / 2.0;
      
      let wealth = currentSavings;
      for (let i = 0; i < monthsToFreedom; i++) {
        wealth *= (1 + monthlyReturn);
        wealth += totalMonthlySavings;
      }
      
      let testWealth = wealth;
      let sustainable = true;
      for (let i = 0; i < monthsInRetirement; i++) {
        testWealth *= (1 + monthlyReturn);
        testWealth -= monthlyCost;
        if (testWealth < 0) {
          sustainable = false;
          break;
        }
      }
      
      if (sustainable) {
        high = totalMonthlySavings;
      } else {
        low = totalMonthlySavings;
      }
    }
    
    return high;
  };

  const calculateProjection = (): void => {
    setIsCalculating(true);
    setErrorMessage('');
    setChartData([]);
    
    setTimeout(() => {
      try {
        const currentAgeInt = parseInt(currentAge);
        const targetFreedomAgeInt = parseInt(targetFreedomAge);
        const income = monthlyIncome!;
        const cost = monthlyCost!;
        const savings = currentSavings!;
        
        if (currentAgeInt >= targetFreedomAgeInt) {
          setErrorMessage('Target freedom age must be greater than current age');
          setIsCalculating(false);
          return;
        }
        
        if (savings < 0) {
          setErrorMessage('Current savings cannot be negative');
          setIsCalculating(false);
          return;
        }
        
        const finalAge = 95;
        const monthlySurplus = income - cost;
        const annualReturn = 0.04;
        const monthlyReturn = Math.pow(1 + annualReturn, 1.0/12.0) - 1;
        
        setDebugMonthlySurplus(monthlySurplus);
        
        const requiredSavings = requiredMonthlySavings(
          currentAgeInt,
          targetFreedomAgeInt,
          savings,
          cost,
          finalAge,
          monthlyReturn
        );
        setRequiredMonthlySavingsAmount(requiredSavings);
        
        const chartDataPoints: ChartData[] = [];
        let currentWealth = savings;
        let idealWealth = savings;
        
        chartDataPoints.push({
          age: currentAgeInt,
          current: currentWealth,
          ideal: idealWealth
        });
        
        for (let year = currentAgeInt + 1; year <= finalAge; year++) {
          for (let month = 1; month <= 12; month++) {
            currentWealth *= (1 + monthlyReturn);
            idealWealth *= (1 + monthlyReturn);
            
            if (year <= targetFreedomAgeInt) {
              currentWealth += monthlySurplus;
              idealWealth += requiredSavings;
            } else {
              currentWealth -= cost;
              idealWealth -= cost;
              
              if (currentWealth < 0) currentWealth = 0;
              if (idealWealth < 0) idealWealth = 0;
            }
          }
          
          chartDataPoints.push({
            age: year,
            current: currentWealth,
            ideal: idealWealth
          });
        }
        
        setChartData(chartDataPoints);
        
        const freedomData = chartDataPoints.find(d => d.age === targetFreedomAgeInt);
        const wealthAtFreedomCurrent = freedomData?.current || 0;
        const wealthAtFreedomIdeal = freedomData?.ideal || 0;
        
        const additionalNeeded = Math.max(0, requiredSavings - monthlySurplus);
        
        // TRACKING AUTOMÁTICO
        trackCalculation({
          currentAge: currentAgeInt,
          targetAge: targetFreedomAgeInt,
          monthlyIncome: income,
          monthlyCost: cost,
          currentSavings: savings,
          wealthAtFreedomCurrent,
          wealthAtFreedomIdeal,
          requiredMonthlySavings: requiredSavings,
          additionalNeeded
        });
        
        setSummary(`📊 CENÁRIO ATUAL (linha vermelha):
💰 Patrimônio aos ${targetFreedomAgeInt} anos: ${currencyFormatterNoDecimals.format(wealthAtFreedomCurrent)}
💵 Sobra mensal atual: ${currencyFormatter.format(monthlySurplus)}

📊 CENÁRIO IDEAL (linha verde):
💰 Patrimônio aos ${targetFreedomAgeInt} anos: ${currencyFormatterNoDecimals.format(wealthAtFreedomIdeal)}
💵 Investimento mensal ideal: ${currencyFormatter.format(requiredSavings)}

💡 Você precisa investir mais ${currencyFormatter.format(additionalNeeded)} por mês

⏰ Anos até liberdade: ${targetFreedomAgeInt - currentAgeInt}
📈 Projeção baseada em 4% de retorno real anual`);
        
        setIsCalculating(false);
      } catch (error) {
        console.error('Error calculating projection:', error);
        setErrorMessage('Erro no cálculo. Verifique os dados inseridos.');
        setIsCalculating(false);
      }
    }, 500);
  };

  const saveCurrentScenario = (): void => {
    if (!currentUser) return;
    
    const currentAgeInt = parseInt(currentAge);
    const targetFreedomAgeInt = parseInt(targetFreedomAge);
    const income = monthlyIncome!;
    const cost = monthlyCost!;
    const savings = currentSavings!;
    
    const wealthAtFreedom = calculateWealthAtFreedom(
      currentAgeInt,
      targetFreedomAgeInt,
      savings,
      income - cost
    );
    
    const scenario: SavedScenario = {
      id: Date.now().toString(),
      name: scenarioName || `Cenário ${new Date().toLocaleDateString('pt-BR')}`,
      currentAge: currentAgeInt,
      targetAge: targetFreedomAgeInt,
      monthlyIncome: income,
      monthlyCost: cost,
      currentSavings: savings,
      createdAt: new Date(),
      wealthAtFreedom,
      monthsToFreedom: (targetFreedomAgeInt - currentAgeInt) * 12,
      additionalNeeded: Math.max(0, requiredMonthlySavingsAmount - (income - cost)),
      userId: currentUser.id
    };
    
    const newScenarios = [...savedScenarios, scenario];
    setSavedScenarios(newScenarios);
    localStorage.setItem(`scenarios_${currentUser.id}`, JSON.stringify(newScenarios));
    
    const updatedUser = { ...currentUser, scenarios: newScenarios };
    setCurrentUser(updatedUser);
    saveUserToGlobal(updatedUser);
    
    markCalculationAsSaved();
    
    setScenarioName('');
    setShowSaveDialog(false);
  };

  const calculateWealthAtFreedom = (
    currentAge: number,
    targetAge: number,
    currentSavings: number,
    monthlySurplus: number
  ): number => {
    const monthsToFreedom = (targetAge - currentAge) * 12;
    const monthlyReturn = Math.pow(1.04, 1.0/12.0) - 1;
    
    let wealth = currentSavings;
    for (let i = 0; i < monthsToFreedom; i++) {
      wealth *= (1 + monthlyReturn);
      wealth += monthlySurplus;
    }
    return wealth;
  };

  const loadScenario = (scenario: SavedScenario): void => {
    setCurrentAge(scenario.currentAge.toString());
    setTargetFreedomAge(scenario.targetAge.toString());
    setMonthlyIncome(scenario.monthlyIncome);
    setMonthlyCost(scenario.monthlyCost);
    setCurrentSavings(scenario.currentSavings);
    
    setShowSavedScenarios(false);
    
    setTimeout(() => {
      calculateProjection();
    }, 100);
  };

  const deleteScenario = (scenarioId: string): void => {
    if (!currentUser) return;
    
    const newScenarios = savedScenarios.filter(s => s.id !== scenarioId);
    setSavedScenarios(newScenarios);
    localStorage.setItem(`scenarios_${currentUser.id}`, JSON.stringify(newScenarios));
    
    const updatedUser = { ...currentUser, scenarios: newScenarios };
    setCurrentUser(updatedUser);
    saveUserToGlobal(updatedUser);
  };

  const formatCurrency = (value: number): string => {
    return currencyFormatter.format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="tooltip">
          <p className="tooltip-label">{`Idade: ${label} anos`}</p>
          <p className="tooltip-current">
            {`Cenário Atual: ${currencyFormatterNoDecimals.format(payload[0].value)}`}
          </p>
          <p className="tooltip-ideal">
            {`Cenário Ideal: ${currencyFormatterNoDecimals.format(payload[1].value)}`}
          </p>
        </div>
      );
    }
    return null;
  };

  // Login Screen Component
  const LoginScreen: React.FC = () => (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <Calculator size={48} className="login-icon" />
          <h1 className="login-title">Legacy Track</h1>
          <p className="login-subtitle">Sua jornada para a liberdade financeira</p>
        </div>
        
        <div className="login-actions">
          <button onClick={createNewUser} className="btn btn-primary login-btn">
            <User size={20} />
            Começar Planejamento
          </button>
          
          <button onClick={enableBankerMode} className="btn btn-secondary login-btn">
            <Crown size={20} />
            Acesso Banker
          </button>
        </div>
        
        <div className="login-features">
          <div className="feature">
            <div className="feature-icon">🎯</div>
            <div className="feature-text">
              <strong>100% Anônimo</strong>
              <span>Seus dados financeiros ficam privados</span>
            </div>
          </div>
          <div className="feature">
            <div className="feature-icon">📊</div>
            <div className="feature-text">
              <strong>Simulações Precisas</strong>
              <span>Calcule quando atingirá a liberdade financeira</span>
            </div>
          </div>
          <div className="feature">
            <div className="feature-icon">🏆</div>
            <div className="feature-text">
              <strong>Ranking Motivacional</strong>
              <span>Compare-se anonimamente com outros investidores</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Banker Dashboard Component
  const BankerDashboard: React.FC = () => {
    const analytics = getAnalyticsData();
    
    return (
      <div className="banker-dashboard">
        <div className="banker-header">
          <h1>🏦 Legacy Track - Dashboard Banker</h1>
          <div className="banker-stats">
            <div className="stat-card">
              <div className="stat-number">{allUsers.length}</div>
              <div className="stat-label">Clientes Ativos</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{analytics.totalCalculations}</div>
              <div className="stat-label">Cálculos Totais</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{analytics.todayCalculations}</div>
              <div className="stat-label">Hoje</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{analytics.conversionRate}%</div>
              <div className="stat-label">Taxa Conversão</div>
            </div>
          </div>
          
          <div className="analytics-summary">
            <div className="analytics-row">
              <div className="analytics-item">
                <span className="analytics-label">👥 Usuários únicos (hoje):</span>
                <span className="analytics-value">{analytics.uniqueUsersToday}</span>
              </div>
              <div className="analytics-item">
                <span className="analytics-label">📊 Idade média:</span>
                <span className="analytics-value">{analytics.averageAge} anos</span>
              </div>
              <div className="analytics-item">
                <span className="analytics-label">💰 Renda média:</span>
                <span className="analytics-value">{currencyFormatterNoDecimals.format(analytics.averageIncome)}</span>
              </div>
              <div className="analytics-item">
                <span className="analytics-label">🎯 Meta média:</span>
                <span className="analytics-value">{analytics.averageTargetAge} anos</span>
              </div>
            </div>
          </div>
        </div>

        <div className="banker-content">
          <div className="clients-panel">
            <h3>👥 Clientes ({allUsers.length})</h3>
            <div className="clients-list">
              {allUsers.map(user => (
                <div 
                  key={user.id} 
                  className={`client-item ${selectedClient?.id === user.id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedClient(user);
                    loadClientMessages(user.id);
                    loadUserScenarios(user.id);
                  }}
                >
                  <div 
                    className="client-avatar" 
                    style={{ backgroundColor: user.avatar }}
                  >
                    {user.anonymousName.charAt(0)}
                  </div>
                  <div className="client-info">
                    <div className="client-name">{user.anonymousName}</div>
                    <div className="client-meta">
                      {user.scenarios.length} cenários • {user.totalCalculations || 0} cálculos
                    </div>
                    <div className="client-last-active">
                      Ativo: {new Date(user.lastActive).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="recent-activity">
              <h4>🔥 Atividade Recente</h4>
              <div className="activity-list">
                {analytics.recentCalculations.slice(0, 5).map((calc: CalculationLog) => (
                  <div key={calc.id} className="activity-item">
                    <div className="activity-user">
                      {calc.userAnonymousName}
                    </div>
                    <div className="activity-details">
                      💰 {currencyFormatterNoDecimals.format(calc.inputIncome)} 
                      → 🎯 {calc.inputTargetAge} anos
                      {calc.wasSaved ? ' ✅' : ' 👁️'}
                    </div>
                    <div className="activity-time">
                      {new Date(calc.timestamp).toLocaleString('pt-BR')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {selectedClient && (
            <div className="client-details">
              <div className="client-header">
                <div 
                  className="client-avatar-large" 
                  style={{ backgroundColor: selectedClient.avatar }}
                >
                  {selectedClient.anonymousName.charAt(0)}
                </div>
                <div>
                  <h3>{selectedClient.anonymousName}</h3>
                  <p>Cliente desde {new Date(selectedClient.createdAt).toLocaleDateString('pt-BR')}</p>
                  <p className="client-stats">
                    📊 {selectedClient.totalCalculations || 0} cálculos realizados
                    • 💾 {selectedClient.scenarios.length} cenários salvos
                  </p>
                </div>
              </div>

              <div className="client-tabs">
                <div className="tab-content">
                  <h4>📊 Cenários Salvos ({savedScenarios.length})</h4>
                  {savedScenarios.length > 0 ? (
                    <div className="scenarios-grid">
                      {savedScenarios.map((scenario: SavedScenario) => (
                        <div key={scenario.id} className="scenario-card-mini">
                          <h5>{scenario.name}</h5>
                          <p>Meta: {scenario.targetAge} anos</p>
                          <p>Patrimônio: {currencyFormatterNoDecimals.format(scenario.wealthAtFreedom)}</p>
                          <small>{new Date(scenario.createdAt).toLocaleDateString('pt-BR')}</small>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="empty-state-text">Nenhum cenário salvo ainda</p>
                  )}

                  <h4>💬 Mensagens</h4>
                  <div className="messages-container">
                    <div className="messages-list">
                      {messages.map(message => (
                        <div key={message.id} className={`message ${message.fromBanker ? 'banker' : 'client'}`}>
                          <div className="message-content">{message.content}</div>
                          <div className="message-time">
                            {new Date(message.createdAt).toLocaleString('pt-BR')}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="message-input">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Digite sua mensagem..."
                        className="form-input"
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      />
                      <button onClick={sendMessage} className="btn btn-primary">
                        <MessageCircle size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render principal
  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  if (userType === 'BANKER' && showBankerDashboard) {
    return <BankerDashboard />;
  }

  return (
    <div className="app">
      <div className="container">
        {/* Header */}
        <div className="card">
          <div className="header">
            <div className="user-info">
              <div 
                className="user-avatar" 
                style={{ backgroundColor: currentUser?.avatar }}
              >
                {currentUser?.anonymousName.charAt(0)}
              </div>
              <div>
                <h1 className="title">
                  <Calculator className="title-icon" />
                  Legacy Track
                </h1>
                <p className="user-name">Olá, {currentUser?.anonymousName}!</p>
              </div>
            </div>
            
            <div className="header-actions">
              {userType === 'BANKER' && (
                <button
                  onClick={() => setShowBankerDashboard(true)}
                  className="btn btn-purple"
                >
                  <Crown size={20} />
                  Dashboard
                </button>
              )}
              
              <button
                onClick={() => setShowSavedScenarios(true)}
                className="btn btn-purple scenarios-btn"
              >
                <Folder size={20} />
                {savedScenarios.length}
              </button>
              
              <button
                onClick={() => setShowRanking(true)}
                className="btn btn-secondary"
              >
                🏆 Ranking
              </button>
              
              <button
                onClick={logout}
                className="btn btn-secondary"
                title="Sair"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Input Form */}
        <div className="card">
          <h2 className="section-title">Informações Básicas</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Idade Atual</label>
              <input
                type="number"
                value={currentAge}
                onChange={(e) => setCurrentAge(e.target.value)}
                className="form-input"
                placeholder="Ex: 30"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Idade Alvo para Liberdade Financeira</label>
              <input
                type="number"
                value={targetFreedomAge}
                onChange={(e) => setTargetFreedomAge(e.target.value)}
                className="form-input"
                placeholder="Ex: 50"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Renda Mensal (R$)</label>
              <input
                type="number"
                step="0.01"
                value={monthlyIncome || ''}
                onChange={(e) => setMonthlyIncome(parseFloat(e.target.value) || null)}
                className="form-input"
                placeholder="Ex: 5000.00"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Custo de Vida Mensal (R$)</label>
              <input
                type="number"
                step="0.01"
                value={monthlyCost || ''}
                onChange={(e) => setMonthlyCost(parseFloat(e.target.value) || null)}
                className="form-input"
                placeholder="Ex: 3000.00"
              />
            </div>
            
            <div className="form-group form-group-full">
              <label className="form-label">Patrimônio Atual (R$)</label>
              <input
                type="number"
                step="0.01"
                value={currentSavings || ''}
                onChange={(e) => setCurrentSavings(parseFloat(e.target.value) || null)}
                className="form-input"
                placeholder="Ex: 50000.00"
              />
            </div>
          </div>
          
          <div className="button-group">
            <button
              onClick={calculateProjection}
              disabled={!inputsAreValid() || isCalculating}
              className="btn btn-primary"
            >
              {isCalculating ? (
                <>
                  <div className="spinner"></div>
                  Calculando...
                </>
              ) : (
                <>
                  <Calculator size={20} />
                  Calcular & Mostrar Gráfico
                </>
              )}
            </button>
            
            {summary && (
              <button
                onClick={() => setShowSaveDialog(true)}
                className="btn btn-success"
              >
                <Save size={20} />
                Salvar Cenário
              </button>
            )}
            
            {summary && (
              <button
                onClick={() => exportToExcel()}
                className="btn btn-purple"
              >
                <Download size={20} />
                Exportar Excel
              </button>
            )}
          </div>
          
          {errorMessage && (
            <div className="error-message">{errorMessage}</div>
          )}
        </div>

        {/* Results */}
        {summary && (
          <div className="card">
            <h2 className="section-title">Resultados</h2>
            
            <div className="summary-box">
              <pre className="summary-text">{summary}</pre>
            </div>

            {/* Chart */}
            {chartData.length > 0 && (
              <div className="chart-section">
                <h3 className="chart-title">Projeção de Patrimônio ao Longo do Tempo</h3>
                
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 60, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="age" 
                        label={{ value: 'Idade', position: 'insideBottom', offset: -10 }}
                        domain={['dataMin', 'dataMax']}
                        type="number"
                        scale="linear"
                      />
                      <YAxis 
                        tickFormatter={(value) => {
                          if (value >= 1000000) {
                            return `R$ ${(value / 1000000).toFixed(1)}M`;
                          } else if (value >= 1000) {
                            return `R$ ${(value / 1000).toFixed(0)}k`;
                          } else {
                            return currencyFormatterNoDecimals.format(value);
                          }
                        }}
                        label={{ value: 'Patrimônio', angle: -90, position: 'insideLeft' }}
                        domain={['auto', 'auto']}
                        width={80}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="current" 
                        stroke="#ef4444" 
                        strokeWidth={3}
                        name="Cenário Atual"
                        dot={false}
                        connectNulls={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="ideal" 
                        stroke="#22c55e" 
                        strokeWidth={3}
                        name="Cenário Ideal"
                        dot={false}
                        connectNulls={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Analysis */}
            <div className="analysis-box">
              <h4 className="analysis-title">Resumo da Análise</h4>
              <div className="analysis-content">
                <p>Sobra Mensal Atual: {formatCurrency(debugMonthlySurplus)}</p>
                <p className="text-green">
                  Investimento Mensal Necessário: {formatCurrency(requiredMonthlySavingsAmount)}
                </p>
                <p className={requiredMonthlySavingsAmount > debugMonthlySurplus ? 'text-red' : 'text-green'}>
                  Adicional Mensal Necessário: {formatCurrency(Math.max(0, requiredMonthlySavingsAmount - debugMonthlySurplus))}
                </p>
                <p className="text-gray">Projeção baseada em 4% de retorno real anual</p>
              </div>
            </div>
          </div>
        )}

        {/* Save Dialog */}
        {showSaveDialog && (
          <div className="modal-overlay">
            <div className="modal">
              <h3 className="modal-title">Salvar Cenário</h3>
              <p className="modal-description">Dê um nome para este cenário de planejamento financeiro</p>
              
              <input
                type="text"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                placeholder="Nome do cenário"
                className="form-input"
              />
              
              <div className="modal-buttons">
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveCurrentScenario}
                  className="btn btn-primary"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Saved Scenarios Modal */}
        {showSavedScenarios && (
          <div className="modal-overlay">
            <div className="modal modal-large">
              <div className="modal-header">
                <h3 className="modal-title">💾 Cenários Salvos</h3>
                <button
                  onClick={() => setShowSavedScenarios(false)}
                  className="btn btn-secondary"
                >
                  Fechar
                </button>
              </div>
              
              <div className="modal-content">
                {savedScenarios.length === 0 ? (
                  <div className="empty-state">
                    <Folder className="empty-icon" size={60} />
                    <h4 className="empty-title">Nenhum cenário salvo</h4>
                    <p className="empty-description">Calcule um planejamento e salve para comparar depois</p>
                  </div>
                ) : (
                  <div className="scenarios-list">
                    {savedScenarios
                      .sort((a: SavedScenario, b: SavedScenario) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .map((scenario: SavedScenario) => (
                        <div key={scenario.id} className="scenario-item">
                          <div className="scenario-header">
                            <h4 className="scenario-name">{scenario.name}</h4>
                            <span className="scenario-date">
                              {new Date(scenario.createdAt).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                          
                          <p className="scenario-description">
                            Liberdade aos {scenario.targetAge} anos • {currencyFormatterNoDecimals.format(scenario.wealthAtFreedom)}
                          </p>
                          
                          <div className="scenario-buttons">
                            <button
                              onClick={() => loadScenario(scenario)}
                              className="btn btn-small btn-purple"
                            >
                              Carregar
                            </button>
                            <button
                              onClick={() => deleteScenario(scenario.id)}
                              className="btn btn-small btn-danger"
                            >
                              Excluir
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Ranking Modal */}
        {showRanking && (
          <div className="modal-overlay">
            <div className="modal modal-large">
              <div className="modal-header">
                <h3 className="modal-title">🏆 Ranking de Liberdade Financeira</h3>
                <button
                  onClick={() => setShowRanking(false)}
                  className="btn btn-secondary"
                >
                  Fechar
                </button>
              </div>
              
              <div className="modal-content">
                <div className="ranking-list">
                  {allUsers
                    .filter(user => user.scenarios.length > 0)
                    .map(user => ({
                      ...user,
                      bestScenario: user.scenarios.reduce((best, scenario) => 
                        scenario.targetAge < best.targetAge ? scenario : best
                      )
                    }))
                    .sort((a, b) => a.bestScenario.targetAge - b.bestScenario.targetAge)
                    .map((user, index) => (
                      <div key={user.id} className={`ranking-item ${user.id === currentUser?.id ? 'current-user' : ''}`}>
                        <div className="ranking-position">
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                        </div>
                        <div 
                          className="ranking-avatar" 
                          style={{ backgroundColor: user.avatar }}
                        >
                          {user.anonymousName.charAt(0)}
                        </div>
                        <div className="ranking-info">
                          <div className="ranking-name">{user.anonymousName}</div>
                          <div className="ranking-stats">
                            🎯 Liberdade aos {user.bestScenario.targetAge} anos
                          </div>
                          <div className="ranking-wealth">
                            💰 {currencyFormatterNoDecimals.format(user.bestScenario.wealthAtFreedom)}
                          </div>
                        </div>
                        {user.isPremium && <div className="premium-badge">👑</div>}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Função exportToExcel removida para evitar erros - será reimplementada se necessário
  const exportToExcel = () => {
    alert('Funcionalidade de exportação será implementada em breve!');
  };
};

export default App;